import {
  AccountGroup,
  AccountInfo,
  BulletPointsList,
  Button,
  Col,
  DefineTermDescription,
  Divider,
  Flex,
  Image,
  LabeledText,
  Row,
  Space,
  Text,
  theme,
} from '@/components';
import { UploadedAttachment } from '@/features/attachment/_components/UploadedAttachment';
import { TagTarget } from '@/features/tag/components';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { type FC, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';

import { useWorkflowStackSiderView } from '@/features/workflow/hooks/stack-sider-view';
import type { WorkflowModel } from '@/features/workflow/models/workflow';
import { datetimeFormat, isRS } from '@/utils';
import { WorkflowDeadlineTag } from '../../common/WorkflowDeadlineTag';

const BUTTON_SIZE = 12;

type Props = {
  workflow: z.infer<typeof WorkflowModel>;
};

const LabeledMonoSpaced = ({ label, children }) => {
  return (
    <Row wrap={false}>
      <Col flex="80px">
        <Text
          text={label}
          variant="footnote"
          weight="regular"
          color="secondary"
        />
      </Col>
      <Col flex="auto">{children}</Col>
    </Row>
  );
};

export const WorkflowDetailsCard: FC<Props> = ({ workflow }) => {
  const { t } = useTranslation();
  const stackSiderView = useWorkflowStackSiderView();

  const siderConfig = stackSiderView.lastItem?.config;

  invariant(
    siderConfig?.viewType === 'workflow-details',
    t('unreachable-code'),
  );

  const { token } = theme.useToken();

  const [isExpanded, toggleExpanded] = useReducer((state) => !state, false);

  return (
    <Flex vertical gap={4}>
      {workflow.deadline && (
        <Flex>
          <WorkflowDeadlineTag deadline={workflow.deadline} />
        </Flex>
      )}

      <Text variant="h4" text={workflow.title} />

      <Space size={2} wrap>
        {workflow.tagTargets?.map((target) => (
          <TagTarget key={target.tagId} tag={target.tag} />
        ))}
      </Space>

      <Flex vertical gap={8}>
        <LabeledMonoSpaced label={t('created-date')}>
          <Text
            text={datetimeFormat({ datetime: workflow.createdAt })}
            variant="footnote"
            weight="regular"
          />
        </LabeledMonoSpaced>

        <LabeledMonoSpaced label={t('author')}>
          <AccountInfo
            accountName={workflow.author.name}
            organizationName={workflow.author.organizations
              ?.map(({ name }) => name)
              .join(',')}
          />
        </LabeledMonoSpaced>

        {isExpanded ? (
          <>
            <LabeledMonoSpaced label={t('assignee')}>
              <AccountGroup
                accountInfoList={workflow.assignees.map((assignee) => ({
                  accountName: assignee.name,
                  organizationName: assignee.organizations
                    ?.map(({ name }) => name)
                    .join(','),
                  iconUrl: assignee.iconDataUrl,
                }))}
              />
            </LabeledMonoSpaced>

            {!isRS() && (
              <LabeledMonoSpaced label={t('viewable-organization')}>
                {!workflow.sharedOrganizations.length ? (
                  <Text text={undefined} variant="footnote" weight="regular" />
                ) : (
                  <BulletPointsList
                    list={workflow.sharedOrganizations.map((organization) => (
                      <Text
                        key={organization.id}
                        text={organization.name}
                        variant="footnote"
                        weight="regular"
                      />
                    ))}
                  />
                )}
              </LabeledMonoSpaced>
            )}

            <Divider />

            <Flex vertical gap={8}>
              <LabeledText
                label={t('remark')}
                text={workflow.remark}
                labelWeight="regular"
                labelColor="secondary"
                labelVariant="footnote"
                variant="footnote"
                weight="regular"
              />

              {workflow.attachments && (
                <Image.PreviewGroup>
                  <DefineTermDescription
                    term={t('attachment-file')}
                    buttonFontSize={token.fontSize}
                    items={workflow.attachments.map((attachment) => (
                      <UploadedAttachment
                        key={attachment.id}
                        attachment={attachment}
                      />
                    ))}
                  />
                </Image.PreviewGroup>
              )}
            </Flex>
          </>
        ) : null}
      </Flex>

      <Flex justify="flex-end">
        <Button
          type="link"
          onClick={toggleExpanded}
          style={{ padding: 0, border: 0, fontSize: token.fontSize }}
        >
          {isExpanded ? (
            <Space>
              <span>{t('close-items')}</span>
              <UpOutlined style={{ fontSize: BUTTON_SIZE }} />
            </Space>
          ) : (
            <Space>
              <span>{t('open-items')}</span>
              <DownOutlined style={{ fontSize: BUTTON_SIZE }} />
            </Space>
          )}
        </Button>
      </Flex>
    </Flex>
  );
};
